import React, { useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../ui/Toast';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const INSTALL_PROMPT_ACCEPTED_KEY = 'pwa-install-accepted';
const INSTALL_PROMPT_DISMISSED_AT_KEY = 'pwa-install-dismissed-at';
const IOS_INSTALL_PROMPT_DISMISSED_AT_KEY = 'pwa-ios-install-dismissed-at';
const INSTALL_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const isStandaloneMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
};

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.innerWidth < 1024;
};

const isIosSafari = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  const isAppleDevice = /iPad|iPhone|iPod/i.test(userAgent) || (userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);
  const isSafariBrowser = /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/i.test(userAgent);

  return isAppleDevice && isSafariBrowser;
};

const shouldShowPrompt = (storageKey: string) => {
  try {
    if (localStorage.getItem(INSTALL_PROMPT_ACCEPTED_KEY) === '1') {
      return false;
    }

    const rawValue = localStorage.getItem(storageKey);
    if (!rawValue) {
      return true;
    }

    const dismissedAt = Number(rawValue);
    if (Number.isNaN(dismissedAt)) {
      return true;
    }

    return Date.now() - dismissedAt > INSTALL_PROMPT_COOLDOWN_MS;
  } catch {
    return true;
  }
};

const markPromptDismissed = (storageKey: string) => {
  try {
    localStorage.setItem(storageKey, String(Date.now()));
  } catch {
    // Ignorujemy bledy localStorage - prompt jest dodatkiem, nie funkcja krytyczna.
  }
};

const markPromptAccepted = () => {
  try {
    localStorage.setItem(INSTALL_PROMPT_ACCEPTED_KEY, '1');
  } catch {
    // Ignorujemy bledy localStorage - prompt jest dodatkiem, nie funkcja krytyczna.
  }
};

const clearPromptAccepted = () => {
  try {
    localStorage.removeItem(INSTALL_PROMPT_ACCEPTED_KEY);
  } catch {
    // Ignorujemy bledy localStorage - prompt jest dodatkiem, nie funkcja krytyczna.
  }
};

const PWAInstallPrompt: React.FC = () => {
  const { state } = useApp();
  const { addToast } = useToast();
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const installToastShownRef = useRef(false);
  const iosToastShownRef = useRef(false);

  useEffect(() => {
    if (!state.isAuthenticated || isStandaloneMode()) {
      return;
    }

    // Jesli uzytkownik usunal ikonke/PWA z telefonu, pozwalamy pokazac prompt ponownie.
    clearPromptAccepted();
  }, [state.isAuthenticated]);

  useEffect(() => {
    const handleAppInstalled = () => {
      markPromptAccepted();
      deferredPromptRef.current = null;
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      if (!state.isAuthenticated || !isMobileDevice() || isStandaloneMode()) {
        return;
      }

      if (installToastShownRef.current || !shouldShowPrompt(INSTALL_PROMPT_DISMISSED_AT_KEY)) {
        return;
      }

      event.preventDefault();
      deferredPromptRef.current = event;

      installToastShownRef.current = true;
      addToast({
        type: 'info',
        title: 'Zainstaluj zadania.app',
        message: 'Dodaj aplikacje do ekranu glownego, aby otwierac ja jak natywna apke.',
        persistent: true,
        actions: [
          {
            label: 'Pozniej',
            onClick: () => {
              markPromptDismissed(INSTALL_PROMPT_DISMISSED_AT_KEY);
            },
          },
          {
            label: 'Zainstaluj',
            variant: 'primary',
            onClick: async () => {
              const deferredPrompt = deferredPromptRef.current;
              if (!deferredPrompt) {
                return;
              }

              try {
                await deferredPrompt.prompt();
                const result = await deferredPrompt.userChoice;

                if (result.outcome === 'accepted') {
                  markPromptAccepted();
                } else {
                  markPromptDismissed(INSTALL_PROMPT_DISMISSED_AT_KEY);
                }
              } catch {
                markPromptDismissed(INSTALL_PROMPT_DISMISSED_AT_KEY);
              } finally {
                deferredPromptRef.current = null;
              }
            },
          },
        ],
      });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [addToast, state.isAuthenticated]);

  useEffect(() => {
    if (!state.isAuthenticated || !isMobileDevice() || isStandaloneMode()) {
      return;
    }

    if (!isIosSafari() || iosToastShownRef.current || !shouldShowPrompt(IOS_INSTALL_PROMPT_DISMISSED_AT_KEY)) {
      return;
    }

    iosToastShownRef.current = true;

    addToast({
      type: 'info',
      title: 'Dodaj do ekranu glownego',
      message: 'Na iPhonie otworz menu Udostepnij w Safari i wybierz "Do ekranu poczatkowego".',
      persistent: true,
      actions: [
        {
          label: 'Rozumiem',
          variant: 'primary',
          onClick: () => {
            markPromptDismissed(IOS_INSTALL_PROMPT_DISMISSED_AT_KEY);
          },
        },
      ],
    });
  }, [addToast, state.isAuthenticated]);

  return null;
};

export default PWAInstallPrompt;
